import { useEffect, useMemo, useRef, useState } from "react";
import { geoConicConformal, geoPath } from "d3-geo";
import europe from "../data/europe.json";
import { ALWAYS_LABEL, CALLOUTS, LABEL_ANCHORS, PRODUCT_LABEL, SMALL_COUNTRIES, UNASSIGNED_CODE, colorFor, countryName, fmt, paymentSplitText } from "../lib/constants";
import { MapHoverCard } from "./MapHoverCard";

const FOCUS = { type: "Polygon", coordinates: [[[5, 34], [5, 55.5], [37, 55.5], [37, 34], [5, 34]]] };

// pending = deals won since the cutoff that are not counted yet (no Venue company) – a faint "+n" ghost next to
// the number; only in the "all venues" view (they have no product split)
const countCells = (countries, view, dark, pending) => {
  const m = {};
  for (const c of countries || []) {
    const n = view === "all" ? c.total : c.products?.[view] || 0;
    // Payments view: provider split ("A 93 · V 24 · Ecom 2") under the number
    const split = view === "Payments" && n && c.payments ? paymentSplitText(c.payments, { short: true }) : "";
    m[c.code] = { show: n > 0, text: fmt(n), color: colorFor(n, dark), pending: 0, split };
  }
  if (view === "all") {
    for (const [code, n] of Object.entries(pending || {})) {
      if (code === UNASSIGNED_CODE || !n) continue;
      m[code] = { ...(m[code] || { show: false, text: "", color: colorFor(0, dark) }), pending: n };
    }
  }
  return m;
};

// Countries with venues are clickable (path and number pill) and open the same country details as the table rows;
// hovering one shows a preview card (venues, products, top owner). The "+n" ghost opens the not-counted list.
export const EuropeMap = ({ countries, view, highlights, onCountryClick, showOwners = false, dark = false, pending = null, onPendingClick }) => {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null);
  // dark board: charcoal borders, dark number bubbles with light text
  const ink = dark
    ? { border: "#0B1220", pill: "#1C2940", pillStroke: "rgba(255,255,255,0.22)", pillShadow: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))", text: "#F8FAFC", sub: "#CBD5E1", code: "#64748B", line: "#94A3B8", ghost: "#94A3B8", ghostStroke: "rgba(148,163,184,0.55)" }
    : { border: "#ffffff", pill: "#ffffff", pillStroke: "rgba(15,23,42,0.14)", pillShadow: "drop-shadow(0 1px 2px rgba(15,23,42,0.28))", text: "#0f172a", sub: "#334155", code: "#94a3b8", line: "#64748b", ghost: "#94a3b8", ghostStroke: "rgba(100,116,139,0.55)" };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      if (r.width > 0 && r.height > 0) setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cells = useMemo(() => countCells(countries, view, dark, pending), [countries, view, dark, pending]);
  const rows = useMemo(() => Object.fromEntries((countries || []).map((c) => [c.code, c])), [countries]);
  const clickable = (code) => !!onCountryClick && !!cells[code]?.show;
  const ghostable = (code) => !!onPendingClick && (cells[code]?.pending || 0) > 0;
  const hint = (code) => `${countryName(code)} · ${cells[code].text} ${view === "all" ? "venues" : PRODUCT_LABEL[view] || view} – click for details`;
  const ghostHint = (code) => `${countryName(code)} · ${fmt(cells[code].pending)} not counted – waiting for a CRM fix – click for the list`;
  // pointer position in (unscaled) map pixels – the TV stage is CSS-scaled, so divide by the rendered scale
  const place = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const k = r.width / size.w || 1;
    return { x: (e.clientX - r.left) / k, y: (e.clientY - r.top) / k };
  };
  const clickProps = (code) =>
    clickable(code)
      ? {
          role: "button",
          tabIndex: 0,
          "aria-label": hint(code),
          onClick: () => {
            setHover(null);
            onCountryClick(code);
          },
          onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && onCountryClick(code),
          onMouseMove: (e) => setHover({ code, ...place(e) }),
          onMouseLeave: () => setHover((h) => (h?.code === code ? null : h)),
        }
      : {};
  const ghostProps = (code) =>
    ghostable(code)
      ? {
          role: "button",
          tabIndex: 0,
          "aria-label": ghostHint(code),
          onClick: (e) => {
            e.stopPropagation();
            setHover(null);
            onPendingClick(code);
          },
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              onPendingClick(code);
            }
          },
          style: { pointerEvents: "auto", cursor: "pointer" },
        }
      : {};

  const { shapes, labels } = useMemo(() => {
    if (!size.w || !size.h) return { shapes: [], labels: [] };
    const projection = geoConicConformal().parallels([40, 55]).rotate([-20, 0]).fitExtent([[6, 6], [size.w - 6, size.h - 6]], FOCUS);
    const path = geoPath(projection);
    const base = Math.max(11, Math.min(size.w, size.h) * 0.032);
    const shapes = [];
    const labels = [];
    for (const f of europe.features) {
      const code = f.properties.code;
      const cell = cells[code];
      shapes.push({ code, d: path(f), color: cell?.color || colorFor(0, dark) });
      if (cell?.show || cell?.pending || ALWAYS_LABEL.includes(code)) {
        const anchor = LABEL_ANCHORS[code] ? projection(LABEL_ANCHORS[code]) : path.centroid(f);
        if (!anchor || Number.isNaN(anchor[0])) continue;
        const callout = CALLOUTS[code] ? projection(CALLOUTS[code]) : null;
        const small = SMALL_COUNTRIES.includes(code);
        const fs = small ? base * 0.7 : base;
        const text = cell?.show ? cell.text : "";
        const ghost = cell?.pending ? `+${fmt(cell.pending)}` : "";
        const gfs = fs * 0.72;
        const mainW = text.length * fs * 0.62;
        const ghostW = ghost ? ghost.length * gfs * 0.62 : 0;
        const gap = text && ghost ? fs * 0.3 : 0;
        const sub = (cell?.show || ghost) && small ? code : null;
        labels.push({
          code, show: !!cell?.show, text, ghost, ghostOnly: !cell?.show && !!ghost, sub, anchor, pos: callout || anchor, callout: !!callout, fs, gfs,
          split: cell?.split || "",
          // number centred left of the ghost, ghost right of the number (both centred when alone)
          mainX: -(gap + ghostW) / 2, ghostX: (mainW + gap) / 2,
          w: mainW + gap + ghostW + fs * 0.9, h: fs * 1.45,
        });
      }
    }
    return { shapes, labels };
  }, [size, cells, dark]);

  return (
    <div ref={wrapRef} className="relative w-full h-full min-h-0">
      {size.w > 0 && (
        <svg data-testid="europe-map-svg" width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`} className="absolute inset-0">
          {shapes.map((s) => (
            <path
              key={s.code}
              data-testid={`country-path-${s.code}`}
              d={s.d}
              fill={s.color}
              stroke={ink.border}
              strokeWidth={1}
              strokeLinejoin="round"
              className={`${highlights?.has(s.code) ? "country-highlight" : "country"} ${clickable(s.code) ? "country-clickable" : ""}`}
              {...clickProps(s.code)}
            >
              {clickable(s.code) && !hover && <title>{hint(s.code)}</title>}
            </path>
          ))}
          {labels.map((l) => (
            <g
              key={l.code}
              data-testid={`country-label-${l.code}`}
              className={clickable(l.code) || l.ghostOnly ? "map-label-clickable" : undefined}
              style={{ pointerEvents: clickable(l.code) || ghostable(l.code) ? "auto" : "none" }}
              {...(l.ghostOnly ? ghostProps(l.code) : clickProps(l.code))}
            >
              {clickable(l.code) && !hover && <title>{hint(l.code)}</title>}
              {l.ghostOnly && <title>{ghostHint(l.code)}</title>}
              {l.callout && <line x1={l.anchor[0]} y1={l.anchor[1]} x2={l.pos[0]} y2={l.pos[1]} stroke={ink.line} strokeWidth={1} />}
              {l.show || l.ghostOnly ? (
                <>
                  <rect
                    x={l.pos[0] - l.w / 2}
                    y={l.pos[1] - l.h / 2}
                    width={l.w}
                    height={l.h}
                    rx={l.h / 2}
                    fill={ink.pill}
                    fillOpacity={l.ghostOnly ? 0.8 : 1}
                    stroke={l.ghostOnly ? ink.ghostStroke : ink.pillStroke}
                    strokeDasharray={l.ghostOnly ? "3 2" : undefined}
                    style={{ filter: l.ghostOnly ? undefined : ink.pillShadow }}
                  />
                  {l.show && (
                    <text x={l.pos[0] + l.mainX} y={l.pos[1] + l.fs * 0.04} textAnchor="middle" dominantBaseline="central" fontSize={l.fs} fontWeight={800} fill={ink.text} className="num">
                      {l.text}
                    </text>
                  )}
                  {l.ghost && (
                    <text
                      data-testid={`country-pending-${l.code}`}
                      x={l.pos[0] + (l.ghostOnly ? 0 : l.ghostX)}
                      y={l.pos[1] + l.fs * 0.06}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={l.gfs}
                      fontWeight={700}
                      fill={ink.ghost}
                      className="num map-ghost"
                      {...(l.ghostOnly ? {} : ghostProps(l.code))}
                    >
                      {!l.ghostOnly && ghostable(l.code) && <title>{ghostHint(l.code)}</title>}
                      {l.ghost}
                    </text>
                  )}
                  {l.sub && (
                    <text x={l.pos[0]} y={l.pos[1] + l.h / 2 + l.fs * 0.78} textAnchor="middle" dominantBaseline="central" fontSize={l.fs * 0.55} fill={ink.sub} className="map-code">
                      {l.sub}
                    </text>
                  )}
                  {l.split && (
                    <text data-testid={`country-pay-split-${l.code}`} x={l.pos[0]} y={l.pos[1] + l.h / 2 + l.fs * (l.sub ? 1.5 : 0.78)} textAnchor="middle" dominantBaseline="central" fontSize={l.fs * 0.5} fontWeight={700} fill={ink.sub} className="num map-code" style={{ paintOrder: "stroke", stroke: ink.pill, strokeWidth: 3, strokeLinejoin: "round" }}>
                      {l.split}
                    </text>
                  )}
                </>
              ) : (
                <text x={l.pos[0]} y={l.pos[1]} textAnchor="middle" dominantBaseline="central" fontSize={l.fs * 0.55} fill={ink.code} className="map-code">
                  {l.code}
                </text>
              )}
            </g>
          ))}
        </svg>
      )}
      {hover && rows[hover.code] && <MapHoverCard country={rows[hover.code]} pos={hover} bounds={size} view={view} showOwners={showOwners} pending={cells[hover.code]?.pending || 0} />}
    </div>
  );
};
